import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { formatErr } from "@/lib/api";
import { Sun, ShieldCheck, Zap } from "lucide-react";

export default function Login() {
    const { login } = useAuth();
    const nav = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        setLoading(true);
        try {
            const u = await login(email, password);
            nav(u.role === "customer" ? "/customer/dashboard" : "/");
        } catch (e) {
            setErr(formatErr(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-white grid grid-cols-1 md:grid-cols-2">
            {/* LEFT — brand + value prop on light gray (hidden on mobile) */}
            <div className="hidden md:flex bg-[#F5F5F5] items-center px-8 sm:px-12 lg:px-20 py-16">
                <div className="max-w-xl w-full space-y-10">
                    <div className="bg-white rounded-md px-3 py-2 inline-block shadow-sm">
                        <img src="/rppl-logo.png" alt="RPPL Solar" className="h-24 w-auto object-contain" />
                    </div>
                    <h2 className="text-[44px] sm:text-[52px] font-semibold tracking-tight leading-[1.05] text-zinc-900">
                        Command your<br />solar fleet from<br />a single console.
                    </h2>
                    <p className="text-[15px] text-zinc-600 leading-relaxed max-w-lg">
                        Solar PV monitoring system for RPPL rooftop installations — real-time
                        production telemetry, prioritized service queue, and executive reporting.
                    </p>
                    <div className="grid grid-cols-3 gap-4 max-w-lg">
                        {[
                            { icon: Zap, label: "Live Power" },
                            { icon: ShieldCheck, label: "Fault Detection" },
                            { icon: Sun, label: "Yield Analytics" },
                        ].map((f) => (
                            <div key={f.label} className="bg-white rounded-2xl p-5 flex flex-col gap-8 min-h-[110px] transition-shadow duration-200 hover:shadow-md">
                                <f.icon className="w-4 h-4 text-zinc-900" strokeWidth={2} />
                                <div className="text-sm font-semibold text-zinc-900">{f.label}</div>
                            </div>
                        ))}
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-400 pt-6">
                        © RPPL Solar · Solar PV Monitoring System
                    </div>
                </div>
            </div>

            {/* RIGHT — sign in form on white */}
            <div className="bg-white flex flex-col items-center justify-center px-6 sm:px-12 lg:px-20 py-16">
                {/* Mobile-only brand mark — anchors the sign-in form when the left panel is hidden */}
                <div className="md:hidden mb-10 bg-[#F5F5F5] rounded-md px-3 py-2 inline-block" data-testid="login-mobile-logo">
                    <img src="/rppl-logo.png" alt="RPPL Solar" className="h-20 w-auto object-contain" />
                </div>
                <form onSubmit={submit} className="w-full max-w-md space-y-8" data-testid="login-form">
                    <div>
                        <h1 className="text-3xl font-semibold tracking-tight text-zinc-900">Operations sign in</h1>
                        <p className="text-sm text-zinc-500 mt-2">Restricted to authorised RPPL personnel.</p>
                    </div>
                    <div className="space-y-6">
                        <div>
                            <label className="text-[10px] font-semibold uppercase tracking-[0.22em] text-zinc-500 mb-2 block">Email</label>
                            <Input
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                type="email"
                                required
                                data-testid="login-email"
                                className="rounded-full h-11 bg-zinc-100 border-transparent px-5 focus-visible:ring-2 focus-visible:ring-[#0A0A0A]"
                            />
                        </div>
                        <div>
                            <label className="text-[10px] font-semibold uppercase tracking-[0.22em] text-zinc-500 mb-2 block">Password</label>
                            <Input
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                type="password"
                                required
                                data-testid="login-password"
                                className="rounded-full h-11 bg-zinc-100 border-transparent px-5 focus-visible:ring-2 focus-visible:ring-[#0A0A0A]"
                            />
                            <Link
                                to="/forgot-password"
                                className="text-[13px] text-zinc-500 hover:text-zinc-900 mt-3 inline-block transition-colors"
                                data-testid="forgot-password-link"
                            >
                                Forgot password?
                            </Link>
                        </div>
                    </div>
                    {err && (
                        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl p-3" data-testid="login-error">
                            {err}
                        </div>
                    )}
                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full h-12 bg-[#0A0A0A] hover:bg-black text-white rounded-full font-semibold transition-transform duration-200 hover:scale-[1.01] disabled:opacity-60"
                        data-testid="login-submit"
                    >
                        {loading ? "Signing in..." : "Sign in to Console"}
                    </button>
                </form>
            </div>
        </div>
    );
}
